import React, { useEffect, useState, useMemo } from "react";
import "./OnlineBooking.css";
import {
  GetConfig,
  GetPaymentServiceDetails,
  UpdateTerms,
  GetSite
} from "./Requests";
import PrivacyPreviewCard from "./B2C";
import CustomButton from "../../Components/Reusable/Button/CustomButton";
import Payments from "./Payments";
import Theme from "./Theme";
import Details from "./Details";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { B2CGB } from "./B2CHTML-en-GB";
import { B2CES } from "./B2CHTML-es";
import { notifyError, notifySuccess } from "../../Response/ResposeHandler";
import { useTranslation } from "react-i18next";

const OnlineBooking = (props) => {
  const { t } = useTranslation("onlineBooking");

  // Use stable keys for tabs and translate only labels for display
  const TAB_KEYS = {
    LINK: "links",
    THEME: "theme",
    DETAILS: "details",
    TANDCS: "tandcs",
    GDPR: "gdpr",
  };

  const [tab, setTab] = useState(TAB_KEYS.LINK);
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState();
  const [tAndCuttonLoading, setTAndCuttonLoading] = useState(false);
  const [status, setStatus] = useState("null");
  const [paymentServiceDetails, setPaymentServiceDetails] = useState();
  const [terms, setTerms] = useState();
  const [site, setSite] = useState("setup");

  useEffect(() => {
    GetConfigWrapper();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function GetConfigWrapper() {
    GetConfig()
      .then((res) => {
        setConfig(res.data);
        setTerms(res.data.terms);
        if (!res.data.payoutsEnabled) setStatus("no payouts");
        if (!res.data.chargesEnabled) setStatus("no charges");
        if (res.data.payoutsEnabled && res.data.chargesEnabled) setStatus("Connected");
      })
      .catch(() => {
        // optionally toast
      })
      .finally(() => {
        GetPaymentServiceDetailsWrapper();
      });
  }

  function GetSiteWrapper() {
    GetSite()
      .then((res) => {
        setSite(res.data);
      })
      .catch(() => {
        // ignore
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function GetPaymentServiceDetailsWrapper() {
    GetPaymentServiceDetails()
      .then((res) => {
        if (res.status === 200) {
          setPaymentServiceDetails(res.data);
        }
      })
      .catch(() => {
        // ignore
      })
      .finally(() => {
        GetSiteWrapper();
      });
  }

  function SubmitTerms() {
    setTAndCuttonLoading(true);
    UpdateTerms(terms)
      .then((res) => {
        if (res.status === 200) {
          notifySuccess(t("onlineBooking.toasts.termsSaved"));
        }
      })
      .catch(() => {
        notifyError(t("onlineBooking.toasts.termsSaveError"));
      })
      .finally(() => {
        setTAndCuttonLoading(false);
      });
  }

  const tabs = useMemo(
    () => [
      { key: TAB_KEYS.LINK, label: t("onlineBooking.tabs.link") },
      { key: TAB_KEYS.THEME,    label: t("onlineBooking.tabs.theme") },
      { key: TAB_KEYS.DETAILS,  label: t("onlineBooking.tabs.details") },
      { key: TAB_KEYS.TANDCS,   label: t("onlineBooking.tabs.tAndCs") },
      { key: TAB_KEYS.GDPR,     label: t("onlineBooking.tabs.gdpr") },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const renderTabs = () => {
    switch (tab) {
      case TAB_KEYS.LINK:
        return (
          <div>
            <ToastContainer />
          <Payments
            GetPaymentServiceDetails={GetPaymentServiceDetailsWrapper}
            setLoading={setIsLoading}
            getConfig={GetConfigWrapper}
            paymentServiceDetails={paymentServiceDetails}
            status={status}
            config={config}
            ShowBookingPortalLink={true}
            ShowPaymentPanel={false}
          />
          </div>
        );
      case TAB_KEYS.THEME:
        return (
          <div>
            <ToastContainer />
        <Theme config={config} site={site} getSite={GetSiteWrapper} />
        </div>
        );
      case TAB_KEYS.DETAILS:
        return (
        <div>
          <ToastContainer />
          <Details config={config} getConfig={GetConfigWrapper} />
        </div>
        );
      case TAB_KEYS.TANDCS:
        return (
          <div className="infoWrap">
            <ToastContainer />
            <section className="infoCard">
              <header className="infoHead" style={{ borderBottom: "none" }}>
                <h3>{t("onlineBooking.terms.title")}</h3>
                <p className="infoSub">{t("onlineBooking.terms.subtitle")}</p>
              </header>
              <div className="infoBody">
                <div className="sm-field">
                  <textarea
                    id="tac-textarea"
                    className="sm-textarea"
                    rows={7}
                    maxLength={500}
                    placeholder={t("onlineBooking.terms.placeholder")}
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                  />
                  <div className="sm-help">
                    {t("onlineBooking.terms.charCount", {
                      count: terms?.length ?? 0,
                      max: 500,
                    })}
                  </div>
                </div>
                <div className="infoActions">
                  <CustomButton
                    disabled={tAndCuttonLoading}
                    className="app-btn--sm"
                    onClick={SubmitTerms}
                    label={t("onlineBooking.actions.save")}
                    loading={tAndCuttonLoading}
                  />
                </div>
              </div>
            </section>
          </div>
        );
      case TAB_KEYS.GDPR:
        return (
          <div className="infoWrap">
            <ToastContainer />
            <PrivacyPreviewCard
              title={t("onlineBooking.privacy.title")}
              noticeHtml={config.bookingPortalLanguage === "en-GB" ? B2CGB : config.bookingPortalLanguage === "es" ? B2CES : B2CGB}
              businessName={config.shopName}
              businessEmail={config.emailForCustomer}
              supportEmail={process.env.REACT_APP_SUPPORT_EMAIL}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* <ToastContainer /> */}
      {isLoading ? (
        <div className="custom-loader-wrapper">
          <div className="custom-loader"></div>
        </div>
      ) : (
        <div className="container">
          <div className="sub-nav" style={{ marginTop: 0 }}>
            {tabs.map((tObj) => (
              <a
                key={tObj.key}
                href="#"
                className={`sub-tab ${tab === tObj.key ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  setTab(tObj.key);
                }}
              >
                {tObj.label}
              </a>
            ))}
          </div>
          {renderTabs()}
        </div>
      )}
    </div>
  );
};

export default OnlineBooking;
